import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { ConsentManager, openConsentManager, loadPreferences, onPreferencesSaved } from '@segment/consent-manager';
import { useRouter } from 'next/router';
import inEU from '@segment/in-eu';
import { useTranslation, Link } from '../../src/i18n';
import { selectIsLoggedInState } from '../../store/selectors';
import route from '../../constants/routes';
import { TP } from '../../constants/consts';
import { renderSnippet } from '../../pages/_document';

const useStyles = makeStyles(() => ({
  cookiesBanner: {
    textAlign: 'justify',
    position: 'fixed',
    bottom: 0,
    width: '100%',
    zIndex: 1000,
  },
  bannerContent: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
    padding: '10px',
  },
  content: {
    display: 'flex',
    textAlign: 'left',
    color: '#fff',
    '& a': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem',
    '& button': {
      marginRight: '10px',
      padding: '5px 10px',
      borderRadius: '4px',
      cursor: 'pointer',
      textDecoration: 'none',
    },
  },
  manageSetting: {
    backgroundColor: '#666',
    color: '#fff',
    border: '1px solid',
  },
  action: {
    backgroundColor: '#0c93b6',
    color: '#fff',
    border: '1px solid #0c93b6 ',
  },
}));

const CookieBanner = ({ isLoggedIn }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { SEGMENT_KEY } = process.env;
  const [isAllowAll, saveAllPreferences] = React.useState(false);
  const [showConsentBanner, showBanner] = React.useState(false);

  const showBannerIfCookieNotAccepted = useCallback(pref => {
    const hasPreferences = typeof pref !== 'undefined' && pref !== null && typeof pref?.customPreferences === 'object';
    const isFalseExist =
      !hasPreferences ||
      Object.values(pref?.customPreferences).length === 0 ||
      Object.values(pref?.customPreferences).some(val => val === false);
    if (isFalseExist) {
      showBanner(true);
    } else {
      showBanner(false);
    }
  }, []);

  const isServer = typeof window === 'undefined';

  const [analyticLoaded, setAnalyticLoaded] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const preferences = loadPreferences();
    const { NODE_ENV } = process.env;

    if (SEGMENT_KEY && !analyticLoaded) {
      const sn = renderSnippet(SEGMENT_KEY, NODE_ENV, router, {});

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = sn;
      script.async = true;
      document.head.append(script);
      setAnalyticLoaded(true);
    }

    if (SEGMENT_KEY && !isServer) {
      setTimeout(() => checkIfCookieBannerExist(), 1600);
      showBannerIfCookieNotAccepted(preferences);
    }
  }, [SEGMENT_KEY, isServer, isLoggedIn, showBannerIfCookieNotAccepted, analyticLoaded, router]);

  if (!SEGMENT_KEY || isServer) {
    return null;
  }

  const checkIfCookieBannerExist = () => {
    const cookieElement = document.getElementById('cookieBanner');
    const firstChild = cookieElement && cookieElement.firstChild;
    const isCookieExist = firstChild && firstChild.hasChildNodes();
    if (isCookieExist) {
      document.body.id = 'has-cookie';
    } else {
      document.body.id = 'has-no-cookie';
    }
  };

  const onSavePreferenceListener = pref => {
    setTimeout(() => checkIfCookieBannerExist(), 700);
    showBannerIfCookieNotAccepted(pref);
  };

  onPreferencesSaved(onSavePreferenceListener);

  const savePreferences = () => {
    saveAllPreferences(true);
  };

  // content
  const bannerContent = (
    <div className={classes.bannerContent}>
      <div className={classes.content}>
        <span>
          {t(`${TP}.Cookie_Banner_Content`)}{' '}
          <Link href={route.COOKIE_POLICY}>
            <a target="_blank">{t(`${TP}.Cookie_Collection_Policy`)}</a>
          </Link>
        </span>
      </div>
      <div className={classes.buttons}>
        <button type="button" className={classes.manageSetting} onClick={openConsentManager}>
          {t(`${TP}.Manage_Settings`)}
        </button>

        <button type="button" className={classes.action} onClick={savePreferences}>
          {t(`${TP}.Accept`)}
        </button>
      </div>
    </div>
  );

  return (
    <div className={classes.cookiesBanner} id="cookieBanner">
      <ConsentManager
        writeKey={SEGMENT_KEY}
        bannerContent={bannerContent}
        bannerSubContent={null}
        preferencesDialogTitle={t(`${TP}.Cookie_Preferences_Dialog_Title`)}
        preferencesDialogContent={t(`${TP}.Cookie_Preferences_Dialog_Content`)}
        cancelDialogTitle={t(`${TP}.Cookie_Cancel_Dialog_Title`)}
        cancelDialogContent={t(`${TP}.Cookie_Cancel_Dialog_Content`)}
        bannerBackgroundColor="#333"
        shouldRequireConsent={inEU}
        allowAll={isAllowAll}
        // disableChooseNo={isLoggedIn}
        showConsentByChoice={showConsentBanner}
      />
    </div>
  );
};

CookieBanner.propTypes = {
  isLoggedIn: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  isLoggedIn: selectIsLoggedInState(),
});
const withConnect = connect(mapStateToProps, null);

export default withConnect(CookieBanner);
